// packages/core/src/services/resourceApi.js
/**
 * Service for fetching and managing generic resource data from the backend API.
 * This core service contains only domain-agnostic CRUD functions.
 * App-specific logic like fetchResourceMap is provided by the app's own resourceApi.
 */
import authService from './authService';
import config from '../config';

/**
 * Deletes a resource by its type and ID.
 * @param {string} resourceType - The type of the resource.
 * @param {string} resourceId - The unique ID of the resource to delete.
 * @returns {Promise<boolean>} - True if deletion was successful (status 204), false otherwise.
 * @throws {Error} If authentication fails, resource not found, or other API errors occur.
 */
export async function deleteResource(resourceType, resourceId) {
  if (!resourceType || !resourceId) {
    throw new Error('Resource type and ID are required for deletion.');
  }
  const url = `${config.API_BASE_URL}/api/v1/resources/${resourceType}/${resourceId}`;
  console.log(`Attempting DELETE: ${url}`);
  try {
    const response = await authService.authFetch(url, { method: 'DELETE' });
    if (response.status === 204) {
      console.log(`Successfully deleted resource ${resourceType}/${resourceId}`);
      return true;
    }
    if (response.status === 404) {
      throw new Error(`Resource not found: ${resourceType}/${resourceId}`);
    }
    if (response.status === 403) {
        throw new Error(`Permission denied to delete resource ${resourceType}/${resourceId}`);
    }
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to delete resource ${resourceType}/${resourceId}. Status: ${response.status}`);
  } catch (error) {
    console.error(`Error deleting resource ${resourceType}/${resourceId}:`, error);
    throw error;
  }
}

/**
 * Creates a new resource.
 * @param {string} resourceType - The type of the resource (e.g., 'collection').
 * @param {object} resourceData - The data for the new resource.
 * @returns {Promise<object>} - The created resource data.
 * @throws {Error} If the request fails.
 */
export async function createResource(resourceType, resourceData) {
  if (!resourceType || !resourceData) {
    throw new Error('Resource type and data are required for creation.');
  }
  const url = `${config.API_BASE_URL}/api/v1/resources/${resourceType}`;
  console.log(`Attempting CREATE: ${url}`, resourceData);
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: resourceData,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to create resource ${resourceType}. Status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error creating resource ${resourceType}:`, error);
    throw error;
  }
}

/**
 * Fetch a resource from the backend by type and ID
 * @param {string} resourceType - The type of resource
 * @param {string} resourceId - The ID of the resource
 * @returns {Promise<Object>} - The resource data
 */
export async function fetchResource(resourceType, resourceId) {
    const url = `${config.API_BASE_URL}/api/v1/resources/${resourceType}/${resourceId}?include_metadata=true`;
    console.log(`🔍 [resourceApi] Fetching ${url}`);
    try {
        const response = await authService.authFetch(url);
        if (!response.ok) {
            if (response.status === 404) {
                console.error(`Resource ${resourceType}/${resourceId} not found`);
                return null;
            }
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `Failed to fetch resource: ${response.status}`);
        }
        const detailResponse = await response.json();
        console.log(`✅ [resourceApi] Successfully fetched ${resourceType}/${resourceId}`, detailResponse);
        const resourceData = detailResponse.resource;
        const userMetadata = detailResponse.user_metadata;
        if (!resourceData) {
            throw new Error("API response did not contain a 'resource' object.");
        }
        if (!resourceData.resource_type) {
            resourceData.resource_type = resourceType;
        }
        const isBookmarked = userMetadata?.bookmarked === true;
        return { ...resourceData, isBookmarked };
    } catch (error) {
        console.error(`❌ [resourceApi] Error fetching ${resourceType}/${resourceId}:`, error);
        throw error;
    }
}

/**
 * Get a list of resources by type
 * @param {string} resourceType - The type of resources to fetch
 * @returns {Promise<Array>} - An array of resources
 */
export async function getResourcesByType(resourceType) {
    const url = `${config.API_BASE_URL}/api/v1/resources/${resourceType}`;
    console.log(`🔍 [resourceApi] Fetching resources of type: ${resourceType}`);
    try {
        const response = await authService.authFetch(url);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `Failed to fetch resources: ${response.status}`);
        }
        const data = await response.json();
        const resources = data.resources || [];
        console.log(`✅ [resourceApi] Successfully fetched ${resources.length} ${resourceType} resources`);
        return resources.map(res => ({ ...res, resource_type: res.resource_type || resourceType }));
    } catch (error) {
        console.error(`❌ [resourceApi] Error fetching ${resourceType} resources:`, error);
        throw error;
    }
}

/**
 * Partially updates a resource by its type and ID.
 * Only the provided fields in partialData will be updated.
 * @param {string} resourceType - The type of the resource.
 * @param {string} resourceId - The unique ID of the resource to patch.
 * @param {object} partialData - An object containing only the fields to update.
 * @returns {Promise<object>} - The updated resource data.
 * @throws {Error} If the request fails.
 */
export async function patchResource(resourceType, resourceId, partialData) {
  if (!resourceType || !resourceId) {
    throw new Error('Resource type and ID are required for patching.');
  }
  const url = `${config.API_BASE_URL}/api/v1/resources/${resourceType}/${resourceId}`;
  console.log(`Attempting PATCH: ${url}`, partialData);
  try {
    const response = await authService.authFetch(url, {
      method: 'PATCH',
      body: partialData,
    });
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`Resource not found: ${resourceType}/${resourceId}`);
      }
      if (response.status === 403) {
        throw new Error(`Permission denied to update resource ${resourceType}/${resourceId}`);
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to patch resource ${resourceType}/${resourceId}. Status: ${response.status}`);
    }
    const updated = await response.json();
    console.log(`✅ [resourceApi] Successfully patched ${resourceType}/${resourceId}`);
    return updated;
  } catch (error) {
    console.error(`❌ [resourceApi] Error patching ${resourceType}/${resourceId}:`, error);
    throw error;
  }
}

/**
 * Check if a resource exists without fetching the full data.
 * @param {string} resourceType - The type of resource.
 * @param {string} resourceId - The ID of the resource.
 * @returns {Promise<boolean>} - Whether the resource exists.
 */
export async function checkResourceExists(resourceType, resourceId) {
    const url = `${config.API_BASE_URL}/api/v1/resources/${resourceType}/${resourceId}`;
    console.log(`🔍 [resourceApi] Checking existence ${url}`);
    try {
        const response = await authService.authFetch(url, { method: 'HEAD' });
        return response.ok;
    } catch (error) {
        if (error.message?.includes('404')) {
             console.warn(`Resource ${resourceType}/${resourceId} check returned 404.`);
             return false;
        }
        console.error(`❌ [resourceApi] Error checking resource ${resourceType}/${resourceId}:`, error);
        return false;
    }
}

/**
 * Fetches a public resource without authentication.
 * @param {string} resourceType - The type of resource (e.g., 'workout_plan').
 * @param {string} resourceId - The ID of the resource.
 * @returns {Promise<Object>} - The resource data including owner_username.
 */
export const fetchPublicResource = async (resourceType, resourceId) => {
  const url = `${config.API_BASE_URL}/api/v1/resources/public/${resourceType}/${resourceId}`;
  console.log(`🔍 [resourceApi] Fetching PUBLIC ${url}`);
  
  try {
    // Standard fetch without auth headers
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Resource not found or is not public.");
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch public resource: ${response.status}`);
    }

    const detailResponse = await response.json();
    const resourceData = detailResponse.resource;
    const ownerUsername = detailResponse.owner_username;

    if (!resourceData) {
      throw new Error("API response did not contain a 'resource' object.");
    }

    // Ensure type is set for the viewer registry
    if (!resourceData.resource_type) {
      resourceData.resource_type = resourceType;
    }
    if (!resourceData.type) {
      resourceData.type = resourceType;
    }

    // Mark as not partial to prevent auto-hydration attempts by viewers
    // Include owner_username for display purposes
    return { ...resourceData, _isPartial: false, owner_username: ownerUsername };
  } catch (error) {
    console.error(`❌ [resourceApi] Error fetching public ${resourceType}/${resourceId}:`, error);
    throw error;
  }
};

/**
 * Generates a social share link (with OG meta tags) for a public resource.
 * The resource must already be public.
 * @param {string} resourceType - The type of the resource (e.g., 'workout_session').
 * @param {string} resourceId - The unique ID of the resource.
 * @returns {Promise<string>} - The social-friendly share URL.
 * @throws {Error} If the resource is not public, not found, or the request fails.
 */
export async function generateShareLink(resourceType, resourceId) {
  if (!resourceType || !resourceId) {
    throw new Error('Resource type and ID are required to generate a share link.');
  }
  const url = `${config.API_BASE_URL}/api/v1/resources/${resourceType}/${resourceId}/share-link`;
  console.log(`🔗 [resourceApi] Generating share link: ${url}`);
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
    });
    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Resource must be public to generate a share link.');
      }
      if (response.status === 404) {
        throw new Error('Resource not found.');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to generate share link. Status: ${response.status}`);
    }
    const data = await response.json();
    console.log(`✅ [resourceApi] Share link generated: ${data.share_url}`);
    return data.share_url;
  } catch (error) {
    console.error(`❌ [resourceApi] Error generating share link for ${resourceType}/${resourceId}:`, error);
    throw error;
  }
}

export default {
  deleteResource,
  fetchResource,
  getResourcesByType,
  createResource,
  patchResource,
  checkResourceExists,
  fetchPublicResource,
  generateShareLink,
};